// kfe-global-playlist.js — the Global Playlist section screen (Brief 090).
//
// PORT TARGET (iOS): GlobalPlaylistViewController.m (Classes/KFEModules/Main).
// A UITableView of queued review items with drag-reorder, soft-delete, an
// autoplay toggle, and continuous playback (tap a row → play; on end →
// auto-advance when autoplay is on). The Global Playlist is the second
// top-level section (design-doc §3, §9): a passive-review queue that fills as
// the learner masters lessons (Briefs 087/088 build the data layer + runtime
// queue; this brief is the screen).
//
// ── World at build time: Brief 091 landed FIRST ──────────────────────────────
// The persistent cross-section player (`services.player`, a PlayerService) is
// already live, so this screen consumes it DIRECTLY rather than building its own
// audio stack (Brief 090 §Decision). Playback survives navigation + gets
// lock-screen controls for free; this screen is the queue editor + transport UI.
// The slim now-playing chrome (<kfe-now-playing>, in the shell) is the
// always-present mini-bar; the bar at the bottom of THIS screen is the full
// transport (prev / play-pause / next + scrubber).
//
// ── iOS divergences in scope (both deliberate) ───────────────────────────────
//   • Per-row affordances (drag handle / play / remove) instead of iOS's
//     table-wide edit mode (design-doc §9 web adaptation).
//   • STICKY soft-delete + a "Removed" restore section (Rule #9 override, §5b/5c;
//     course-designer ratification 2026-06-01). iOS auto-resurrects on
//     re-mastery; the web treats deletion as agentic intent.
//
// ── Test seams ───────────────────────────────────────────────────────────────
// `.store` (the learner-store GP API) and `.player` (the PlayerService) default
// to services.learner / services.player but are injectable as props, so the
// queue + playback logic is unit-testable with fakes (same pattern as
// <kfe-now-playing>).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";

export class KFEGlobalPlaylist extends KFEComponent {
  static props = ["trackId", "store", "player"];

  static styles = `
    :host { display: block; padding-bottom: 6rem; /* clear the player bar */ }

    .empty {
      max-width: 32rem; margin: 3rem auto 0; text-align: center;
      color: var(--kfe-muted); line-height: 1.5;
    }
    .empty h2 { color: var(--kfe-fg); font-size: var(--kfe-h2); margin: 0 0 0.5rem; }

    /* Stay in the phone-width centre column on desktop (design-doc §7.3/§5). */
    .section { max-width: 40rem; margin: 0 auto; }

    .controls {
      display: flex; align-items: center; justify-content: flex-end;
      gap: 0.5rem; padding: 0.5rem; color: var(--kfe-muted);
    }
    .autoplay { display: inline-flex; align-items: center; gap: 0.4rem;
                font-size: 0.9rem; cursor: pointer; -webkit-tap-highlight-color: transparent; }
    .autoplay input { accent-color: var(--kfe-accent); }

    .queue { list-style: none; margin: 0; padding: 0; }

    .removed { margin-top: 1.5rem; }
    .removed-toggle {
      width: 100%; text-align: left; background: transparent; border: 0;
      color: var(--kfe-muted); font-size: 0.9rem; padding: 0.6rem 0.5rem;
      cursor: pointer; border-top: 1px solid var(--kfe-border);
      display: flex; align-items: center; gap: 0.4rem;
    }
    .removed-toggle .chev { transition: transform 0.15s ease; }
    .removed-toggle[aria-expanded="true"] .chev { transform: rotate(90deg); }

    /* Persistent in-section transport bar — single column, pinned to the bottom
       of the centre column. */
    .player-bar {
      position: fixed; left: 0; right: 0; bottom: 3.25rem; /* above the tab bar */
      margin: 0 auto; max-width: 40rem;
      background: var(--kfe-bg-header); border-top: 1px solid var(--kfe-border);
      padding: 0.5rem 0.75rem 0.6rem;
      display: flex; flex-direction: column; gap: 0.25rem;
    }
    .player-bar[hidden] { display: none; }
    .pb-row { display: flex; align-items: center; gap: 0.5rem; }
    .pb-title { flex: 1; min-width: 0; font-size: 0.9rem; font-weight: 600;
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .pb-row button {
      flex: none; background: transparent; border: 0; color: var(--kfe-fg);
      cursor: pointer; font-size: 1.3rem; line-height: 1; padding: 0 0.25rem;
      -webkit-tap-highlight-color: transparent;
    }
    .pb-row .pb-play { font-size: 1.6rem; color: var(--kfe-accent); }
  `;

  constructor() {
    super();
    /** @type {Array<{itemId:number,title:string,audioUrl:string|undefined}>} */
    this._queue = [];
    /** @type {Array<{itemId:number,title:string,audioUrl:string|undefined}>} */
    this._removed = [];
    this._autoplay = false;
    this._removedOpen = false;
    this._loaded = false;
    /** @type {null | { fromIndex:number, pointerId:number, currentIndex:number, rowEl:HTMLElement }} */
    this._drag = null;
    this._cued = false;
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
  }

  get _store() { return /** @type {any} */ (this).store || services.learner; }
  get _player() { return /** @type {any} */ (this).player || services.player; }

  async onConnected() {
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("globalPlaylist.title") },
      bubbles: true,
      composed: true,
    }));

    // Re-render on player state changes (track switch, play/pause, queue load).
    // These are infrequent; timeupdate is handled separately (scrubber-only) to
    // avoid 4×/sec full re-renders.
    const onState = () => { if (!this._drag) this._scheduleRender(); };
    for (const ev of ["trackchange", "playstate", "queuechange"]) {
      this._player.addEventListener(ev, onState);
      this.addCleanup(() => this._player.removeEventListener(ev, onState));
    }
    const onTime = () => this._syncScrubber();
    this._player.addEventListener("timeupdate", onTime);
    this.addCleanup(() => this._player.removeEventListener("timeupdate", onTime));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    await this._loadQueue();
  }

  /**
   * Pull the queue + removed rows + autoplay flag from the store and sync the
   * persistent player. Called on mount and after every queue mutation.
   */
  async _loadQueue() {
    const [queue, removed, autoplay] = await Promise.all([
      this._store.getGlobalPlaylist(),
      this._store.getRemovedGlobalPlaylist(),
      this._store.getGlobalPlaylistAutoplay(),
    ]);
    this._queue = Array.isArray(queue) ? queue : [];
    this._removed = Array.isArray(removed) ? removed : [];
    this._autoplay = !!autoplay;
    this._loaded = true;
    // Keep the persistent player's queue in sync (does NOT auto-start — the
    // engine is untouched, so an in-flight track keeps playing).
    this._player.loadQueue(this._queue, { autoplay: this._autoplay });
    this._scheduleRender();
    // Deep-link cue runs once the rows exist.
    if (!this._cued) queueMicrotask(() => this._cueDeepLink());
  }

  // ── Row intents ────────────────────────────────────────────────────────────

  /** @param {number} index */
  _play(index) {
    const track = this._queue[index];
    if (!track) return;
    const player = this._player;
    // Tapping the active row toggles; tapping a different row starts it.
    if (player.currentTrack && player.currentTrack.itemId === track.itemId) {
      player.togglePlay();
    } else {
      player.playAt(index);
    }
  }

  /** @param {number} index */
  async _remove(index) {
    const track = this._queue[index];
    if (!track) return;
    await this._store.removeFromGlobalPlaylist(track.itemId);
    await this._loadQueue();
  }

  /** @param {number} index */
  async _restore(index) {
    const track = this._removed[index];
    if (!track) return;
    await this._store.restoreFromGlobalPlaylist(track.itemId);
    await this._loadQueue();
  }

  /**
   * Commit a drag-reorder: move the item at `fromIndex` to `toIndex` and
   * re-sequence via the Brief 088 API, then reload. Kept as a discrete method so
   * the reorder wiring is unit-testable without synthesising a full pointer drag.
   * @param {number} fromIndex @param {number} toIndex
   */
  async _commitReorder(fromIndex, toIndex) {
    const track = this._queue[fromIndex];
    if (!track || fromIndex === toIndex) return;
    await this._store.reorderGlobalPlaylist(track.itemId, toIndex);
    await this._loadQueue();
  }

  async _toggleAutoplay() {
    this._autoplay = !this._autoplay;
    await this._store.setGlobalPlaylistAutoplay(this._autoplay);
    this._player.setAutoplay(this._autoplay);
    this._scheduleRender();
  }

  _toggleRemoved() {
    this._removedOpen = !this._removedOpen;
    this._scheduleRender();
  }

  // ── Drag-reorder (pointer; the screen owns the gesture across row shadows) ───

  /** @param {CustomEvent<{index:number,pointerId:number,clientY:number}>} e */
  _onRowGrab = (e) => {
    if (this._drag) return;
    const { index, pointerId } = e.detail;
    const rowEl = this._rowEls()[index];
    if (!rowEl) return;
    this._drag = { fromIndex: index, pointerId, currentIndex: index, rowEl };
    rowEl.classList.add("dragging");
    try { this.setPointerCapture(pointerId); } catch { /* unsupported */ }
    this.addEventListener("pointermove", this._onPointerMove);
    this.addEventListener("pointerup", this._onPointerUp);
    this.addEventListener("pointercancel", this._onPointerUp);
  };

  /** @param {PointerEvent} e */
  _onPointerMove(e) {
    if (!this._drag || e.pointerId !== this._drag.pointerId) return;
    const rows = this._rowEls();
    const drag = this._drag;
    // Find the row whose vertical midpoint the pointer has crossed.
    let target = drag.currentIndex;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      const mid = r.top + r.height / 2;
      if (e.clientY < mid) { target = i; break; }
      target = i;
    }
    if (target !== drag.currentIndex) {
      // Live DOM reorder for feedback: move the dragged row element among its
      // siblings. The authoritative re-sequence happens on drop.
      const list = drag.rowEl.parentNode;
      if (list) {
        const ref = rows[target];
        if (target > drag.currentIndex) list.insertBefore(drag.rowEl, ref.nextSibling);
        else list.insertBefore(drag.rowEl, ref);
      }
      drag.currentIndex = target;
    }
  }

  /** @param {PointerEvent} e */
  _onPointerUp(e) {
    if (!this._drag || e.pointerId !== this._drag.pointerId) return;
    const { fromIndex, currentIndex, rowEl, pointerId } = this._drag;
    rowEl.classList.remove("dragging");
    try { this.releasePointerCapture(pointerId); } catch { /* not captured */ }
    this.removeEventListener("pointermove", this._onPointerMove);
    this.removeEventListener("pointerup", this._onPointerUp);
    this.removeEventListener("pointercancel", this._onPointerUp);
    this._drag = null;
    if (currentIndex !== fromIndex) this._commitReorder(fromIndex, currentIndex);
    else this._scheduleRender(); // restore canonical DOM order after a no-op drag
  }

  // ── View helpers ─────────────────────────────────────────────────────────

  /** @returns {HTMLElement[]} the active-queue row elements, in DOM order. */
  _rowEls() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.shadowRoot?.querySelectorAll(".queue kfe-playlist-row") ?? [])
    );
  }

  _syncScrubber() {
    const bar = /** @type {any} */ (this.shadowRoot?.querySelector("kfe-music-bar"));
    if (!bar) return;
    const eng = this._player.engine;
    bar.currentTimeSec = eng?.currentTime ?? 0;
    bar.durationSec = eng?.duration ?? 0;
  }

  /** Deep-link (/playlist/:trackId): scroll to + highlight the cued row. */
  _cueDeepLink() {
    const trackId = Number(/** @type {any} */ (this).trackId);
    if (!trackId || this._cued) return;
    const idx = this._queue.findIndex((q) => q.itemId === trackId);
    if (idx < 0) return;
    this._cued = true;
    const rowEl = this._rowEls()[idx];
    if (!rowEl) return;
    rowEl.scrollIntoView({ block: "center", behavior: "smooth" });
    rowEl.classList.add("active");
  }

  render() {
    if (!this._loaded) return html``;

    if (this._queue.length === 0 && this._removed.length === 0) {
      return html`
        <div class="empty">
          <h2>${t("globalPlaylist.title")}</h2>
          <p>${t("globalPlaylist.empty")}</p>
        </div>
      `;
    }

    const player = this._player;
    const current = player.currentTrack;
    const playing = player.isPlaying;

    // Active queue rows.
    const list = document.createElement("div");
    list.className = "queue";
    list.setAttribute("role", "list");
    this._queue.forEach((track, i) => {
      const row = /** @type {any} */ (document.createElement("kfe-playlist-row"));
      row.index = i;
      row.title = track.title || String(track.itemId);
      row.active = !!current && current.itemId === track.itemId;
      row.playing = playing;
      row.dataset.itemId = String(track.itemId);
      list.appendChild(row);
    });
    list.addEventListener("play", (e) => this._play(/** @type {any} */ (e).detail.index));
    list.addEventListener("remove", (e) => this._remove(/** @type {any} */ (e).detail.index));
    list.addEventListener("row-grab", (e) => this._onRowGrab(/** @type {any} */ (e)));

    // Removed section (only when ≥1 soft-deleted row — never permanent weight).
    let removedSection = document.createDocumentFragment();
    if (this._removed.length > 0) {
      const sec = document.createElement("div");
      sec.className = "removed";
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "removed-toggle";
      toggle.setAttribute("aria-expanded", String(this._removedOpen));
      toggle.innerHTML = `<span class="chev" aria-hidden="true">›</span>`;
      toggle.append(document.createTextNode(
        t("globalPlaylist.removedSection", { count: this._removed.length })));
      toggle.addEventListener("click", () => this._toggleRemoved());
      sec.appendChild(toggle);
      if (this._removedOpen) {
        const rlist = document.createElement("div");
        rlist.className = "queue removed-list";
        rlist.setAttribute("role", "list");
        this._removed.forEach((track, i) => {
          const row = /** @type {any} */ (document.createElement("kfe-playlist-row"));
          row.index = i;
          row.title = track.title || String(track.itemId);
          row.removed = true;
          rlist.appendChild(row);
        });
        rlist.addEventListener("restore", (e) => this._restore(/** @type {any} */ (e).detail.index));
        sec.appendChild(rlist);
      }
      removedSection = /** @type {any} */ (sec);
    }

    // Transport bar — rendered only when a track is loaded (the now-playing
    // chrome in the shell covers the idle case). `engine` is the lazily-created
    // backend; read its time/duration if it exists yet (no track → still null).
    const eng = player.isActive ? player.engine : null;
    const bar = player.isActive
      ? html`
        <div class="player-bar">
          <div class="pb-row">
            <span class="pb-title">${current?.title || ""}</span>
            <button class="pb-prev" type="button"
              aria-label="${t("globalPlaylist.previous")}"
              on:click=${() => player.previous()}>⏮</button>
            <button class="pb-play" type="button"
              aria-label="${playing ? t("globalPlaylist.pause") : t("globalPlaylist.play")}"
              on:click=${() => player.togglePlay()}>${playing ? "⏸" : "▶"}</button>
            <button class="pb-next" type="button"
              aria-label="${t("globalPlaylist.next")}"
              on:click=${() => player.next()}>⏭</button>
          </div>
          <kfe-music-bar
            .currentTimeSec=${eng?.currentTime ?? 0}
            .durationSec=${eng?.duration ?? 0}
            on:seek=${(/** @type {CustomEvent<number>} */ ev) => player.seekTo(ev.detail)}
          ></kfe-music-bar>
        </div>`
      : document.createDocumentFragment();

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = this._autoplay;
    checkbox.addEventListener("change", () => this._toggleAutoplay());

    return html`
      <div class="section">
        <div class="controls">
          <label class="autoplay">
            ${checkbox}
            ${t("globalPlaylist.autoplay")}
          </label>
        </div>
        ${list}
        ${removedSection}
      </div>
      ${bar}
    `;
  }
}
